import { C1_BaseObject } from "./C1_BaseObject";
import { freeWallPlacementFaces } from "../furniture/C1_freeWallGeometry";
import { C1_Furniture } from "./C1_Furniture";
import { C1_Opening } from "./C1_Opening";
import type { C1_Project } from "./C1_Project";
import type {
  C1_FurnitureData,
  C1_OpeningData,
  C1_PointData,
  C1_RoomData,
  WallSide,
} from "../types/C1_ModelTypes";
import type { Rect } from "../types/C1_AppTypes";
import type { C1_EloItem_Data } from "../types/C1_EloTypes";
import { ensureEloItemIds } from "./C1_ensureEloItemIds";
import { roundToCentimeter } from "../lib/C1_layout";
import {
  applyOpeningContactDefault,
  createEquipmentInstance,
  reconcileEloEquipmentInstance,
} from "./C1_ensureEloEquipmentDefaults";
import { getEloItemProfile, getRoomEloItemProfile } from "../data/C1_eloItemsRegistry";
import {
  C1_FloorFinish,
  RoomType,
  isSpecialExteriorRoomType,
  type C1_FloorFinish as C1_FloorFinishType,
} from "../types/C1_ModelTypes";
import type EstadaRoom from "../../../src/models/Room";
import { c1_state } from "./C1_runtimeContext";
import BaseModel from "../../../src/models/BaseModel";
import ObjectFactory from "../../../src/models/ObjectFactory";
import Group from "../../../src/models/Group";
import { getMaterializedEloRoot, materializeInteractiveEloItem, replaceMaterializedEloDefinitionTree } from "./C1_materializeEloDefinition";
import { C1_Wall } from "./C1_Wall";
import { isLightGroupDefinitionId, isSingleLightDefinitionId, syncSingleLight, syncLightGroup } from "../furniture/C1_lightGroup";
import { isStandaloneEloProfile } from "../data/C1_EloItemCatalog";
import { C1_Consumer } from "./C1_Consumer";
import type { C1_Button } from "./C1_Button";
import type { C1_EloGroup } from "./C1_EloGroup";
import { hasContinuousExteriorWalls } from "../types/C1_RoomTypes";
import { findPolygonPlacement, type PolygonPlacement, type PolygonPoint } from "../lib/C1_polygonPlacement";
import { createRoomPlacementGeometry } from "../furniture/C1_roomPlacementGeometry";
import { roomWallThickness } from "./C1_wallThickness";
import { polygonBoundsTransform } from "../lib/C1_resizePolygonBounds";
import { validateRoomPolygon } from "../lib/C1_validateRoomPolygon";
import { replaceRoomPolygon } from "./C1_applyRoomPlacement";

const MIN_ROOM_SIZE = 1;
const OPENING_PLACEMENT_STEP = 0.1;
export const C1_COORDINATE_MATCH_TOLERANCE = 0.001;
// Matching nearby walls must not erase a short, perpendicular polygon edge.
const WALL_ZERO_LENGTH_TOLERANCE = 1e-7;

/** Determines the wall classification before adjacent wall partnerships are applied. */
function defaultWallIsExterior(roomType: RoomType): boolean {
  return hasContinuousExteriorWalls(roomType) || !isSpecialExteriorRoomType(roomType);
}

export type C1_WallSegment = {
  start: number;
  end: number;
  exterior: boolean;
  /** Wall type inherited from an adjacent non-special room. */
  inheritedFromNormalRoom?: boolean;
};

export type C1_HiddenWallSource = {
  room: C1_Room;
  side: WallSide;
};

export type C1_WallPlacement = {
  room: C1_Room;
  side: WallSide;
  wallId: string;
  offset: number;
};

export class C1_Room extends C1_BaseObject<C1_RoomData> {
  private placementCache?: { key: string; result: PolygonPlacement | undefined };
  private initialHiddenWalls: WallSide[];
  override get owner(): EstadaRoom | undefined {
    return super.owner as EstadaRoom | undefined;
  }

  firstSelection = false;

  get furnitures(): C1_Furniture[] {
    const result: C1_Furniture[] = [];
    if (!this.owner) return result;
    for (const group of this.owner.groups) {
      if (group.groupType === "furniture" && group.c1 instanceof C1_Furniture) result.push(group.c1);
    }
    for (const consumer of this.owner.consumers) {
      if (consumer.c1 instanceof C1_Consumer && consumer.c1.data.lightGroupFurniture) {
        const lightGroup = consumer.c1.lightGroup;
        if (lightGroup) result.push(lightGroup);
      }
    }
    return result;
  }

  /** Independently placed devices retain their own identity outside the room equipment definition. */
  get interactiveEloItems(): Array<C1_Consumer | C1_Button | C1_EloGroup> {
    const result: Array<C1_Consumer | C1_Button | C1_EloGroup> = [];
    if (!this.owner) return result;
    for (const item of [...this.owner.consumers.asArray(), ...this.owner.buttons.asArray(), ...this.owner.groups.asArray()]) {
      const facet = item.c1;
      if (facet?.className === "C1_Consumer" || facet?.className === "C1_Button" || facet?.className === "C1_EloGroup") {
        const deviceFacet = facet as C1_Consumer | C1_Button | C1_EloGroup;
        if (deviceFacet.interactivCreated && !(deviceFacet instanceof C1_Consumer && deviceFacet.data.lightGroupFurniture)) result.push(deviceFacet);
      }
    }
    return result;
  }

  get openings(): C1_Opening[] {
    const result: C1_Opening[] = [];
    if (!this.owner) return result;
    for (const group of this.owner.groups) {
      if (group.groupType === "opening" && group.c1 instanceof C1_Opening) result.push(group.c1);
    }
    return result;
  }

  /** Stable ordered wall collection persisted on the owning ESTADA room. */
  get walls() {
    return this.owner?.walls;
  }

  get className(): string {
    return "C1_Room";
  }

  constructor(data: Omit<C1_RoomData, "id">, owner?: BaseModel) {
    const { hiddenWalls = [], ...persistentData } = data;
    super(persistentData, owner);
    this.initialHiddenWalls = [...hiddenWalls];
    // this.furnitures = furnitures.map((item) => new C1_Furniture(item, this));
    // this.openings = openings.map((item) => new C1_Opening(item, this));
  }

  get type() {
    return this.data.type;
  }

  get floorFinish(): C1_FloorFinishType {
    return this.data.floorFinish ?? C1_FloorFinish.None;
  }

  get daliAsDefault() {
    return this.data.daliAsDefault ?? false;
  }


  get hiddenWalls() {
    const hidden: WallSide[] = [];
    const sides: WallSide[] = ["top", "right", "bottom", "left"];
    for (const side of sides) {
      if (this.wallForSide(side)?.visible === false) hidden.push(side);
    }
    return hidden;
  }

  get eloTree() {
    return getMaterializedEloRoot(this.owner as EstadaRoom | undefined, "room")?.toLegacyTreeData();
  }

  /** The room editor owns only equipment from the room profile, never independent placements. */
  get definitionEloTree() {
    const root = getMaterializedEloRoot(this.owner, "room");
    if (!root) return undefined;
    root.setTemporaryItems(root.items.filter(item => !item.interactivCreated && item._defId.startsWith(`${root._defId}/`)));
    return root.toLegacyTreeData();
  }

  get roomTextPosition(): { x: number; y: number } {
    if (this.data.roomTextPosition) return this.data.roomTextPosition;
    const rect = this.rect();
    const point = this.automaticPlacementPoint();
    // Keep the label clear of the central light; reduce the offset in narrow rooms.
    const offsetX = 0.35;
    const offsetY = 0.5;
    const offsetScale = Math.min(1, Math.max(0, this.placementCache?.result?.clearance ?? 0) * 0.8 / Math.hypot(offsetX, offsetY));
    const width = rect.x2 - rect.x1;
    const height = rect.y2 - rect.y1;
    return {
      x: width > 0 ? (point.x + offsetX * offsetScale - rect.x1) / width : 0.5,
      y: height > 0 ? (point.y + offsetY * offsetScale - rect.y1) / height : 0.5,
    };
  }

  /** Shares a cached polygon anchor between automatic room labels, equipment and exports. */
  automaticPlacementPoint(project: C1_Project = c1_state.project): PolygonPoint {
    const polygon = this.polygonPoints();
    const wallInsets: number[] = [];
    for (const wall of this.walls ?? []) {
      const solid = !isSpecialExteriorRoomType(this.type) || this.wallInheritsExteriorFromNormalRoom(wall);
      const thickness = roomWallThickness(this, wall, project);
      wallInsets.push(wall.visible && solid ? thickness / 2 : 0);
    }
    const key = JSON.stringify([polygon, wallInsets]);
    if (this.placementCache?.key !== key) {
      const result = findPolygonPlacement(polygon, {
        wallInsets,
        minimumClearance: 0.3,
        precision: 0.01,
        preferredPoint: this.placementCache?.result?.point,
      });
      this.placementCache = { key, result };
    }
    // An unfinished/degenerate room has no interior; keep its controls at its first vertex.
    return { ...(this.placementCache.result?.point ?? polygon[0]) };
  }

  /** Stores the room label position relative to the current room bounds. */
  setRoomTextPosition(position: { x: number; y: number }): void {
    const current = this.roomTextPosition;
    if (current.x === position.x && current.y === position.y) return;
    this.beforeChange("roomTextPosition");
    this._data.roomTextPosition = { ...position };
    this.changed("roomTextPosition");
  }

  setEloTree(eloTree?: C1_EloItem_Data) {
    this.beforeChange("eloItems");
    replaceMaterializedEloDefinitionTree(this.owner as EstadaRoom | undefined, eloTree);
    this.changed("eloItems");
  }

  setType(type: RoomType) {
    if (type !== this.data.type) {
      this.beforeChange("type");
      this._data.type = type;
      const project = c1_state.project;
      const nextEloTree = reconcileEloEquipmentInstance(
        "room",
        getRoomEloItemProfile(type),
        project.equipmentStandard,
        this.eloTree,
        project.settings.buttonDefaults,
      );
      replaceMaterializedEloDefinitionTree(this.owner as EstadaRoom | undefined, nextEloTree);
      this.changed("type");
      this.recalculateFloorWallTypes();
    }
  }

  setFloorFinish(floorFinish: C1_FloorFinishType) {
    if (floorFinish === this.floorFinish) return;
    this.beforeChange("floorFinish");
    this._data.floorFinish = floorFinish;
    this.changed("floorFinish");
  }

  setDaliAsDefault(daliAsDefault: boolean) {
    if (daliAsDefault !== this.daliAsDefault) {
      this.beforeChange("daliAsDefault");
      this._data.daliAsDefault = daliAsDefault;
      this.changed("daliAsDefault");
    }
  }


  setWallHidden(side: WallSide, hidden: boolean) {
    this.wallForSide(side)?.setVisible(!hidden);
  }

  hiddenWallSources(side: WallSide, project: C1_Project, start?: number, end?: number): C1_HiddenWallSource[] {
    const floor = project.floors.find((candidate) => candidate.rooms.some((room) => room.id === this.id));
    if (!floor) return [];

    const rect = this.rect();
    const vertical = side === "left" || side === "right";
    const line = vertical ? (side === "left" ? rect.x1 : rect.x2) : side === "top" ? rect.y1 : rect.y2;
    const wallStart = start ?? (vertical ? rect.y1 : rect.x1);
    const wallEnd = end ?? (vertical ? rect.y2 : rect.x2);
    const tolerance = 0.001;

    return floor.rooms.flatMap((candidate) => {
      if (!candidate.hiddenWalls.length) return [];
      const candidateRect = candidate.rect();

      return candidate.hiddenWalls.flatMap((hiddenSide) => {
        const hiddenVertical = hiddenSide === "left" || hiddenSide === "right";
        if (hiddenVertical !== vertical) return [];

        const hiddenLine = hiddenVertical
          ? hiddenSide === "left"
            ? candidateRect.x1
            : candidateRect.x2
          : hiddenSide === "top"
            ? candidateRect.y1
            : candidateRect.y2;
        if (Math.abs(hiddenLine - line) > tolerance) return [];

        const hiddenStart = hiddenVertical ? candidateRect.y1 : candidateRect.x1;
        const hiddenEnd = hiddenVertical ? candidateRect.y2 : candidateRect.x2;
        return hiddenStart < wallEnd - tolerance && hiddenEnd > wallStart + tolerance
          ? [{ room: candidate, side: hiddenSide }]
          : [];
      });
    });
  }

  isWallHidden(side: WallSide, project: C1_Project, start?: number, end?: number) {
    return this.hiddenWallSources(side, project, start, end).length > 0;
  }

  width() {
    const rect = this.rect();
    return rect.x2 - rect.x1;
  }

  height() {
    const rect = this.rect();
    return rect.y2 - rect.y1;
  }

  setWidth(width: number) {
    const rect = this.rect();
    this.setRect({ ...rect, x2: rect.x1 + width });
  }

  setHeight(height: number) {
    const rect = this.rect();
    this.setRect({ ...rect, y2: rect.y1 + height });
  }

  setSize(width: number, height: number) {
    const rect = this.rect();
    this.setRect({ ...rect, x2: rect.x1 + width, y2: rect.y1 + height });
  }

  /** Returns the usable inner rectangle; hidden wall sides extend to their wall axis. */
  innerRect(roomRect: Rect = this.rect(), project: C1_Project = c1_state.project): Rect {
    const halfWallThickness = (side: WallSide): number => {
      if (this.isWallHidden(side, project)) return 0;
      let thickness = this.isExteriorWall(side)
        ? project.settings.wallThicknessExterior
        : project.settings.wallThicknessInterior;
      for (const wall of this.walls ?? []) {
        if (wall.visible && this.compatibilitySide(wall) === side)
          thickness = Math.max(thickness, roomWallThickness(this, wall, project));
      }
      return thickness / 2;
    };

    return {
      x1: roomRect.x1 + halfWallThickness("left"),
      x2: roomRect.x2 - halfWallThickness("right"),
      y1: roomRect.y1 + halfWallThickness("top"),
      y2: roomRect.y2 - halfWallThickness("bottom"),
    };
  }

  /** Actual interior wall faces used for furniture and independent electrical placement. */
  placementGeometry(project: C1_Project = c1_state.project) {
    const geometry = createRoomPlacementGeometry([...(this.walls ?? [])].flatMap(wall => {
      const points = this.wallPoints(wall);
      if (!points || Math.hypot(points.end.x - points.start.x, points.end.y - points.start.y) < 1e-9) return [];
      const solid = !isSpecialExteriorRoomType(this.type) || this.wallInheritsExteriorFromNormalRoom(wall);
      const thickness = roomWallThickness(this, wall, project);
      return [{ ...points, wallId: wall.id, visible: wall.visible, side: this.compatibilitySide(wall),
        inset: wall.visible && solid ? thickness / 2 : 0 }];
    }));
    for (const wall of this.owner?.parentFloor?.c1?.freeWalls ?? []) {
      geometry.walls.push(...freeWallPlacementFaces(wall, project.settings.wallThicknessInterior, geometry.polygon));
    }
    return geometry;
  }

  /** Usable polygon area in square metres, including wall thickness and hidden wall faces. */
  interiorArea(project: C1_Project = c1_state.project): number {
    const signedArea = (points: readonly { x: number; y: number }[]) => points.reduce((sum, point, index) => {
      const next = points[(index + 1) % points.length];
      return sum + point.x * next.y - next.x * point.y;
    }, 0) / 2;
    const axisArea = signedArea(this.polygonPoints());
    const innerArea = signedArea(this.placementGeometry(project).polygon);
    // Faces crossing in a collapsed room reverse winding rather than creating usable space.
    return Math.max(0, Math.min(Math.abs(axisArea), innerArea * Math.sign(axisArea)));
  }

  /** Finds the nearest visible wall position in this room for an opening of the given width. */
  nearestWallPlacementInRoom(
    point: { x: number; y: number },
    openingWidth: number,
    snapToStep = true,
  ): C1_WallPlacement | null {
    let nearest: { wall: C1_Wall; side: WallSide; distance: number; offset: number } | undefined;
    if (!this.walls) return null;
    for (const wall of this.walls) {
      if (!wall.visible) continue;
      const wallPoints = this.wallPoints(wall);
      if (!wallPoints) continue;
      const dx = wallPoints.end.x - wallPoints.start.x;
      const dy = wallPoints.end.y - wallPoints.start.y;
      const length = Math.hypot(dx, dy);
      if (length < C1_COORDINATE_MATCH_TOLERANCE) continue;

      const projectedOffset =
        ((point.x - wallPoints.start.x) * dx + (point.y - wallPoints.start.y) * dy) / (length * length);
      const minimumOffset = Math.min(openingWidth / 2 / length, 0.5);
      const distanceAlongWall = projectedOffset * length;
      const placedDistance = snapToStep
        ? Math.round(distanceAlongWall / OPENING_PLACEMENT_STEP) * OPENING_PLACEMENT_STEP
        : distanceAlongWall;
      const offset = Math.min(Math.max(placedDistance / length, minimumOffset), 1 - minimumOffset);
      const projectedPoint = {
        x: wallPoints.start.x + dx * offset,
        y: wallPoints.start.y + dy * offset,
      };
      const distance = Math.hypot(point.x - projectedPoint.x, point.y - projectedPoint.y);
      const side = this.compatibilitySideForWall(wall, dx, dy);
      if (!nearest || distance < nearest.distance) nearest = { wall, side, distance, offset };
    }
    if (!nearest) return null;

    return {
      room: this,
      side: nearest.side,
      wallId: nearest.wall.id,
      offset: Math.min(Math.max(nearest.offset, 0), 1),
    };
  }

  /** Projects an arbitrary segment onto the former four-side vocabulary used by existing views. */
  private compatibilitySideForWall(wall: C1_Wall, dx: number, dy: number): WallSide {
    void wall;
    const points = this.polygonPoints();
    const signedArea = points.reduce((area, point, index) => {
      const next = points[(index + 1) % points.length];
      return area + point.x * next.y - next.x * point.y;
    }, 0);
    // Side names encode which side of the wall contains the room, regardless
    // of polygon winding or the first segment of a rectangular room.
    const direction = signedArea < 0 ? -1 : 1;
    if (Math.abs(dx) >= Math.abs(dy)) return dx * direction >= 0 ? "top" : "bottom";
    return dy * direction >= 0 ? "right" : "left";
  }

  /** Side projection retained for editor components that still use four side names. */
  compatibilitySide(wall: C1_Wall): WallSide {
    const points = this.wallPoints(wall);
    if (!points) return "top";
    return this.compatibilitySideForWall(
      wall,
      points.end.x - points.start.x,
      points.end.y - points.start.y,
    );
  }

  setRect(rect: Rect, _project: C1_Project = c1_state.project, _snappingDisabled = false, preserveExactSize = false) {
    void _project;
    void _snappingDisabled;
    const normalizeCoordinate = (value: number) =>
      preserveExactSize ? Math.round(value * 1_000_000) / 1_000_000 : roundToCentimeter(value);
    const width = normalizeCoordinate(rect.x2 - rect.x1);
    const height = normalizeCoordinate(rect.y2 - rect.y1);
    const nextRect = {
      ...rect,
      x2: rect.x1 + Math.max(width, MIN_ROOM_SIZE),
      y2: rect.y1 + Math.max(height, MIN_ROOM_SIZE),
    };
    if (this.walls && this.walls.size >= 3) {
      const transform = polygonBoundsTransform(this.polygonPoints(), this.rect(), nextRect);
      // Capture both sides before changing endpoints or rebuilding shared segments.
      const partners = [...this.walls].flatMap(wall => {
        const partner = wall.partnerWall;
        const room = partner?.parent?.c1 as C1_Room | undefined;
        const points = partner && room?.wallPoints(partner);
        return partner && room && points ? [{ room, wall: partner, start: transform(points.start), end: transform(points.end) }] : [];
      });
      const endpoints = [...this.walls].map(wall => ({ wall, end: transform(wall.end) }));
      this.setStartPoint(transform(this.data.startPoint));
      for (const { wall, end } of endpoints) wall.setEnd(end);
      for (const { room, wall, start, end } of partners) {
        const index = room.walls?.indexOf(wall) ?? -1;
        if (index < 0) continue;
        room.moveWallStart(index, start);
        wall.setEnd(end);
        if (index === room.walls!.size - 1) room.setStartPoint(end);
      }
      this.recalculateFloorWallTypes();
      return;
    }
    this.setStartPoint({ x: nextRect.x1, y: nextRect.y1 });
    this.setRectangularWallEndpoints([
      { x: nextRect.x2, y: nextRect.y1 },
      { x: nextRect.x2, y: nextRect.y2 },
      { x: nextRect.x1, y: nextRect.y2 },
      { x: nextRect.x1, y: nextRect.y1 },
    ]);
    for (const side of this.initialHiddenWalls) this.wallForSide(side)?.setVisible(false);
    this.initialHiddenWalls = [];
    this.recalculateFloorWallTypes();
  }

  /** Replaces part of one wall with an orthogonal extrusion; the caller rebuilds floor relationships afterward. */
  extrudeWallSegment(
    wallId: string,
    side: WallSide,
    segmentStart: number,
    segmentEnd: number,
    targetCoordinate: number,
  ): void {
    const walls = this.walls;
    const sourceWall = walls?.findFirst((wall) => wall.id === wallId);
    if (!walls || !sourceWall) return;
    const sourcePoints = this.wallPoints(sourceWall);
    if (!sourcePoints) return;

    const vertical = side === "left" || side === "right";
    const start = Math.min(segmentStart, segmentEnd);
    const end = Math.max(segmentStart, segmentEnd);
    if (end - start < C1_COORDINATE_MATCH_TOLERANCE) return;

    const sourceIndex = walls.indexOf(sourceWall);
    const previousWall = walls.at(sourceIndex === 0 ? walls.size - 1 : sourceIndex - 1);
    const nextWall = walls.at(sourceIndex === walls.size - 1 ? 0 : sourceIndex + 1);
    const previousIsCollinear = previousWall ? this.wallsAreGeometricallyCollinear(previousWall, sourceWall) : false;
    const nextIsCollinear = nextWall ? this.wallsAreGeometricallyCollinear(sourceWall, nextWall) : false;

    const attachedOpenings: Array<{ opening: C1_Opening; point: { x: number; y: number } }> = [];
    for (const opening of this.openings) {
      if (opening.wall?.id !== sourceWall.id) continue;
      attachedOpenings.push({ opening, point: opening.openingCenter() });
    }

    const targetStart = vertical
      ? { x: targetCoordinate, y: sourcePoints.start.y }
      : { x: sourcePoints.start.x, y: targetCoordinate };
    const targetEnd = vertical
      ? { x: targetCoordinate, y: sourcePoints.end.y }
      : { x: sourcePoints.end.x, y: targetCoordinate };

    if (!previousIsCollinear) this.moveWallStart(sourceIndex, targetStart);
    if (!nextIsCollinear && sourceIndex === walls.size - 1) this.setStartPoint(targetEnd);

    const requestedEndpoints = [targetStart, targetEnd];
    if (nextIsCollinear) requestedEndpoints.push({ ...sourcePoints.end });
    const endpoints: Array<{ x: number; y: number }> = [];
    let precedingPoint = previousIsCollinear ? sourcePoints.start : targetStart;
    for (const endpoint of requestedEndpoints) {
      if (Math.hypot(endpoint.x - precedingPoint.x, endpoint.y - precedingPoint.y) < WALL_ZERO_LENGTH_TOLERANCE) {
        continue;
      }
      endpoints.push(endpoint);
      precedingPoint = endpoint;
    }

    sourceWall.delete();
    const replacementWalls: C1_Wall[] = [];
    for (let index = 0; index < endpoints.length; index += 1) {
      const replacement = new C1_Wall(
        {
          end: endpoints[index],
          visible: sourceWall.visible,
          exterior: sourceWall.exterior,
        },
        this.owner,
      );
      walls.insert(sourceIndex + index, replacement);
      replacementWalls.push(replacement);
    }

    for (const attached of attachedOpenings) {
      const alongCoordinate = vertical ? attached.point.y : attached.point.x;
      const targetPoint =
        alongCoordinate >= start - C1_COORDINATE_MATCH_TOLERANCE &&
        alongCoordinate <= end + C1_COORDINATE_MATCH_TOLERANCE
          ? vertical
            ? { x: targetCoordinate, y: alongCoordinate }
            : { x: alongCoordinate, y: targetCoordinate }
          : attached.point;
      const placement = this.nearestPlacementOnWalls(targetPoint, replacementWalls);
      if (placement) attached.opening.setWallPlacement(placement.wall, placement.offset, attached.opening.reflect);
    }
    this.normalizeWallSegments();
  }

  /** Reports whether moving this wall needs a new connector at a collinear polygon seam. */
  wallHasCollinearNeighbor(wall: C1_Wall): boolean {
    const walls = this.walls;
    if (!walls) return false;
    const index = walls.indexOf(wall);
    if (index < 0) return false;
    const previous = walls.at(index === 0 ? walls.size - 1 : index - 1);
    const next = walls.at(index === walls.size - 1 ? 0 : index + 1);
    return Boolean(
      (previous && this.wallsAreGeometricallyCollinear(previous, wall)) ||
      (next && this.wallsAreGeometricallyCollinear(wall, next)),
    );
  }

  /** Moves a wall's shared start vertex while preserving a closed polygon at the storage seam. */
  private moveWallStart(wallIndex: number, point: { x: number; y: number }): void {
    const walls = this.walls;
    if (!walls) return;
    if (wallIndex > 0) {
      walls.at(wallIndex - 1)?.setEnd(point);
      return;
    }
    this.setStartPoint(point);
    walls.at(walls.size - 1)?.setEnd(point);
  }

  /** Tests only geometric collinearity of two consecutive walls. */
  private wallsAreGeometricallyCollinear(first: C1_Wall, second: C1_Wall): boolean {
    const firstPoints = this.wallPoints(first);
    const secondPoints = this.wallPoints(second);
    if (!firstPoints || !secondPoints) return false;
    const firstVector = {
      x: firstPoints.end.x - firstPoints.start.x,
      y: firstPoints.end.y - firstPoints.start.y,
    };
    const secondVector = {
      x: secondPoints.end.x - secondPoints.start.x,
      y: secondPoints.end.y - secondPoints.start.y,
    };
    const firstLength = Math.hypot(firstVector.x, firstVector.y);
    const secondLength = Math.hypot(secondVector.x, secondVector.y);
    if (firstLength < C1_COORDINATE_MATCH_TOLERANCE || secondLength < C1_COORDINATE_MATCH_TOLERANCE) return false;
    const cross = Math.abs(firstVector.x * secondVector.y - firstVector.y * secondVector.x);
    return cross <= C1_COORDINATE_MATCH_TOLERANCE * firstLength * secondLength;
  }

  /** Merges consecutive collinear walls with identical properties and preserves their object attachments. */
  normalizeWallSegments(): void {
    const walls = this.walls;
    if (!walls || walls.size < 2) return;

    let index = 0;
    while (index < walls.size - 1) {
      const first = walls.at(index);
      const second = walls.at(index + 1);
      if (!first || !second || !this.wallsCanMerge(first, second)) {
        index += 1;
        continue;
      }
      this.mergeWallPair(first, second);
      if (index > 0) index -= 1;
    }
    this.mergeClosingWallPair();
  }

  /** Merges the last and first walls, which are adjacent across the polygon's storage seam. */
  private mergeClosingWallPair(): void {
    const walls = this.walls;
    if (!walls || walls.size < 2) return;
    const first = walls.at(0);
    const last = walls.at(walls.size - 1);
    if (!first || !last || !this.wallsCanMerge(last, first)) return;

    const attachedOpenings = this.captureOpeningsOnWalls(last, first);
    const attachedFurniture = this.captureFurnitureOnWalls(last, first);
    const combinedEnd = { ...first.end };
    this.setStartPoint(combinedEnd);
    first.delete();
    last.setEnd(combinedEnd);
    this.restoreWallAttachments(last, attachedOpenings, attachedFurniture);
  }

  /** Checks geometry, direction and all persisted wall-specific properties. */
  private wallsCanMerge(first: C1_Wall, second: C1_Wall): boolean {
    if (first.visible !== second.visible || first.exterior !== second.exterior) return false;
    if (first.partnerWall || second.partnerWall) return false;
    const firstPoints = this.wallPoints(first);
    const secondPoints = this.wallPoints(second);
    if (!firstPoints || !secondPoints) return false;
    const firstVector = {
      x: firstPoints.end.x - firstPoints.start.x,
      y: firstPoints.end.y - firstPoints.start.y,
    };
    const secondVector = {
      x: secondPoints.end.x - secondPoints.start.x,
      y: secondPoints.end.y - secondPoints.start.y,
    };
    const firstLength = Math.hypot(firstVector.x, firstVector.y);
    const secondLength = Math.hypot(secondVector.x, secondVector.y);
    if (firstLength < WALL_ZERO_LENGTH_TOLERANCE || secondLength < WALL_ZERO_LENGTH_TOLERANCE) return true;
    const cross = Math.abs(firstVector.x * secondVector.y - firstVector.y * secondVector.x);
    const collinear = cross <= C1_COORDINATE_MATCH_TOLERANCE * firstLength * secondLength;
    const sameDirection = firstVector.x * secondVector.x + firstVector.y * secondVector.y > 0;
    return collinear && sameDirection;
  }

  /** Extends the first wall, removes the second and projects attached objects onto the survivor. */
  private mergeWallPair(first: C1_Wall, second: C1_Wall): void {
    const attachedOpenings = this.captureOpeningsOnWalls(first, second);
    const attachedFurniture = this.captureFurnitureOnWalls(first, second);

    first.setEnd(second.end);
    second.delete();
    this.restoreWallAttachments(first, attachedOpenings, attachedFurniture);
  }

  /** Captures opening centers before their source walls are changed or removed. */
  private captureOpeningsOnWalls(
    first: C1_Wall,
    second: C1_Wall,
  ): Array<{ opening: C1_Opening; point: { x: number; y: number } }> {
    const attached: Array<{ opening: C1_Opening; point: { x: number; y: number } }> = [];
    for (const opening of this.openings) {
      if (opening.wall === first || opening.wall === second) attached.push({ opening, point: opening.openingCenter() });
    }
    return attached;
  }

  /** Captures furniture references before their source walls are changed or removed. */
  private captureFurnitureOnWalls(first: C1_Wall, second: C1_Wall): C1_Furniture[] {
    const attached: C1_Furniture[] = [];
    for (const furniture of this.furnitures) {
      if (furniture.dockWall === first || furniture.dockWall === second) attached.push(furniture);
    }
    return attached;
  }

  /** Reattaches openings and furniture to a merged wall without changing their geometric positions. */
  private restoreWallAttachments(
    wall: C1_Wall,
    openings: Array<{ opening: C1_Opening; point: { x: number; y: number } }>,
    furniture: C1_Furniture[],
  ): void {
    const mergedPoints = this.wallPoints(wall);
    if (mergedPoints) {
      const dx = mergedPoints.end.x - mergedPoints.start.x;
      const dy = mergedPoints.end.y - mergedPoints.start.y;
      const lengthSquared = dx * dx + dy * dy;
      for (const attached of openings) {
        const offset = lengthSquared === 0
          ? 0
          : ((attached.point.x - mergedPoints.start.x) * dx + (attached.point.y - mergedPoints.start.y) * dy) /
            lengthSquared;
        attached.opening.setWallPlacement(wall, Math.min(Math.max(offset, 0), 1), attached.opening.reflect);
      }
    }
    for (const item of furniture) item.dockWall = wall;
  }

  /** Splits one persisted wall at geometric partner endpoints and preserves its attachments. */
  private splitWallAtOffsets(wall: C1_Wall, offsets: number[]): void {
    const walls = this.walls;
    const points = this.wallPoints(wall);
    if (!walls || !points || offsets.length === 0) return;
    const sourceIndex = walls.indexOf(wall);
    if (sourceIndex < 0) return;

    const attachedOpenings = this.captureOpeningsOnWalls(wall, wall);
    const attachedFurniture = this.captureFurnitureOnWalls(wall, wall);
    const sortedOffsets = [...offsets].sort((left, right) => left - right);
    const endpoints = [...sortedOffsets, 1].map((offset) => ({
      x: points.start.x + (points.end.x - points.start.x) * offset,
      y: points.start.y + (points.end.y - points.start.y) * offset,
    }));
    const replacements = [wall];
    wall.partnerWall = undefined;
    wall.setEnd(endpoints[0]);
    for (let index = 1; index < endpoints.length; index += 1) {
      const segment = new C1_Wall(
        { end: endpoints[index], visible: wall.visible, exterior: wall.exterior },
        this.owner,
      );
      walls.insert(sourceIndex + index, segment);
      replacements.push(segment);
    }

    for (const attached of attachedOpenings) {
      const placement = this.nearestPlacementOnWalls(attached.point, replacements);
      if (placement) attached.opening.setWallPlacement(placement.wall, placement.offset, attached.opening.reflect);
    }
    for (const furniture of attachedFurniture) {
      const position = furniture.equipmentPostitionGeometric(furniture.id);
      const placement = position ? this.nearestPlacementOnWalls(position, replacements) : undefined;
      furniture.dockWall = placement?.wall ?? replacements[0];
    }
  }

  /** Rebuilds physical wall segments at every adjacent-room boundary endpoint. */
  private splitWallsForPartners(rooms: C1_Room[]): void {
    const walls = this.walls?.asArray() ?? [];
    for (const wall of walls) {
      const points = this.wallPoints(wall);
      if (!points) continue;
      const dx = points.end.x - points.start.x;
      const dy = points.end.y - points.start.y;
      const lengthSquared = dx * dx + dy * dy;
      if (lengthSquared <= C1_COORDINATE_MATCH_TOLERANCE ** 2) continue;
      const normalizedTolerance = C1_COORDINATE_MATCH_TOLERANCE / Math.sqrt(lengthSquared);
      const offsets: number[] = [];
      for (const otherRoom of rooms) {
        if (otherRoom === this || !otherRoom.walls) continue;
        for (const otherWall of otherRoom.walls) {
          const other = otherRoom.wallPoints(otherWall);
          if (!other) continue;
          const crossStart = Math.abs(dx * (other.start.y - points.start.y) - dy * (other.start.x - points.start.x));
          const crossEnd = Math.abs(dx * (other.end.y - points.start.y) - dy * (other.end.x - points.start.x));
          const tolerance = C1_COORDINATE_MATCH_TOLERANCE * Math.sqrt(lengthSquared);
          if (crossStart > tolerance || crossEnd > tolerance) continue;
          for (const endpoint of [other.start, other.end]) {
            const offset = ((endpoint.x - points.start.x) * dx + (endpoint.y - points.start.y) * dy) / lengthSquared;
            if (offset <= normalizedTolerance || offset >= 1 - normalizedTolerance) continue;
            if (!offsets.some((existing) => Math.abs(existing - offset) <= normalizedTolerance)) {
              offsets.push(offset);
            }
          }
        }
      }
      this.splitWallAtOffsets(wall, offsets);
    }
  }

  /** Recomputes persisted one-to-one partner references from actual wall geometry. */
  recalculateFloorWallTypes(): void {
    const floor = this.owner?.parentFloor;
    if (!floor) return;
    const rooms: C1_Room[] = [];
    for (const room of floor.rooms) {
      if (room.c1 instanceof C1_Room) rooms.push(room.c1);
    }
    for (const room of rooms) {
      room.validateWallBoundary();
      room.prepareWallSegmentsForRelationshipRebuild();
    }
    for (const room of rooms) {
      room.splitWallsForPartners(rooms);
    }

    const segments: Array<{ room: C1_Room; wall: C1_Wall; points: WallEndpoints }> = [];
    for (const room of rooms) {
      if (!room.walls) continue;
      for (const wall of room.walls) {
        wall.partnerWall = undefined;
        wall.setExterior(defaultWallIsExterior(room.type));
        const points = room.wallPoints(wall);
        if (points) segments.push({ room, wall, points });
      }
    }
    for (let leftIndex = 0; leftIndex < segments.length; leftIndex += 1) {
      const left = segments[leftIndex];
      if (left.wall.partnerWall) continue;
      for (let rightIndex = leftIndex + 1; rightIndex < segments.length; rightIndex += 1) {
        const right = segments[rightIndex];
        if (right.room === left.room || right.wall.partnerWall) continue;
        const sameDirection = pointsMatch(left.points.start, right.points.start) && pointsMatch(left.points.end, right.points.end);
        const oppositeDirection = pointsMatch(left.points.start, right.points.end) && pointsMatch(left.points.end, right.points.start);
        if (!sameDirection && !oppositeDirection) continue;
        left.wall.partnerWall = right.wall;
        right.wall.partnerWall = left.wall;
        const leftIsSpecial = isSpecialExteriorRoomType(left.room.type);
        const rightIsSpecial = isSpecialExteriorRoomType(right.room.type);
        const solidExteriorBoundary = hasContinuousExteriorWalls(left.room.type) || hasContinuousExteriorWalls(right.room.type);
        const specialExteriorBoundary = solidExteriorBoundary || leftIsSpecial !== rightIsSpecial;
        left.wall.setExterior(specialExteriorBoundary);
        right.wall.setExterior(specialExteriorBoundary);
        break;
      }
    }
  }

  /** Simplifies unusable wall loops before shared segments are rebuilt, in the caller's undo step. */
  validateWallBoundary(): void {
    if (!this.owner || !this.walls || this.walls.size < 3) return;
    const points = this.polygonPoints();
    const polygon = validateRoomPolygon(points, c1_state.project.settings.wallThicknessInterior);
    // Fully collapsed rooms are handled by resolveCollapsedWallMove, including rollback/merge.
    if (!polygon?.length) return;
    const original = points.slice(0, -1);
    const same = original.length === polygon.length && original.some((_, offset) => polygon.every((p, i) => {
      const q = original[(offset + i) % original.length];
      return Math.hypot(p.x - q.x, p.y - q.y) < 1e-7;
    }));
    if (same) return;
    const openings = this.openings.map(item => ({ item, point: item.absolutePosition }));
    const furniture = this.furnitures.map(item => ({ item, point: item.equipmentPostitionGeometric(item.id), docked: !!item.dockWall }));
    const devices = this.interactiveEloItems.map(item => ({ item, point: item.independentAbsolutePosition }));
    replaceRoomPolygon(this, polygon);
    for (const { item, point } of openings) {
      const placement = this.nearestWallPlacementInRoom(point, item.width, false);
      const wall = placement && this.walls.findFirst(candidate => candidate.id === placement.wallId);
      if (wall && placement) item.setWallPlacement(wall, placement.offset, item.reflect);
    }
    const rect = this.innerRect();
    for (const { item, point, docked } of furniture) {
      if (!point) continue;
      const placement = docked ? this.nearestWallPlacementInRoom(point, 0, false) : undefined;
      item.setPosition({ x: (point.x - rect.x1) / (rect.x2 - rect.x1), y: (point.y - rect.y1) / (rect.y2 - rect.y1) },
        placement?.side, item.reflect, placement ? item.wallDocked : undefined, placement?.wallId);
    }
    for (const { item, point } of devices) if (point) item.moveInteractiveTo(point);
  }

  /** Removes degenerate segments and merges the remaining raw geometry before partner walls are rebuilt. */
  private prepareWallSegmentsForRelationshipRebuild(): void {
    if (!this.walls) return;
    const defaultExterior = defaultWallIsExterior(this.type);
    for (const wall of this.walls) {
      wall.partnerWall = undefined;
      wall.setExterior(defaultExterior);
    }
    this.removeZeroLengthWallSegments();
    this.normalizeWallSegments();
  }

  /** Deletes zero-length walls and transfers their attached objects to the nearest surviving segment. */
  private removeZeroLengthWallSegments(): void {
    const walls = this.walls;
    if (!walls) return;
    const candidates = walls.asArray();
    for (const wall of candidates) {
      if (walls.indexOf(wall) < 0) continue;
      const points = this.wallPoints(wall);
      if (!points) continue;
      const length = Math.hypot(points.end.x - points.start.x, points.end.y - points.start.y);
      if (length >= WALL_ZERO_LENGTH_TOLERANCE) continue;

      const attachedOpenings = this.captureOpeningsOnWalls(wall, wall);
      const attachedFurniture = this.captureFurnitureOnWalls(wall, wall);
      const furniturePositions = new Map<C1_Furniture, { x: number; y: number } | undefined>();
      for (const furniture of attachedFurniture) {
        furniturePositions.set(furniture, furniture.equipmentPostitionGeometric(furniture.id));
      }
      wall.delete();

      const survivingWalls = walls.asArray();
      for (const attached of attachedOpenings) {
        const placement = this.nearestPlacementOnWalls(attached.point, survivingWalls);
        if (placement) attached.opening.setWallPlacement(placement.wall, placement.offset, attached.opening.reflect);
      }
      for (const furniture of attachedFurniture) {
        const position = furniturePositions.get(furniture);
        const placement = position ? this.nearestPlacementOnWalls(position, survivingWalls) : undefined;
        furniture.dockWall = placement?.wall;
      }
    }
  }

  /** Derives one wall type from the complete current floor geometry. */
  isExteriorSegment(wall: C1_Wall): boolean {
    if (hasContinuousExteriorWalls(this.type)) return true;
    const partnerRoom = wall.partnerWall?.parent?.c1;
    if (partnerRoom instanceof C1_Room && hasContinuousExteriorWalls(partnerRoom.type)) return true;
    return wall.exterior !== false;
  }

  /** Reports whether a special room has its own solid shell or shares a solid neighboring wall. */
  wallInheritsExteriorFromNormalRoom(wall: C1_Wall): boolean {
    if (hasContinuousExteriorWalls(this.type)) return true;
    if (!isSpecialExteriorRoomType(this.type)) return false;
    const partnerRoom = wall.partnerWall?.parent?.c1;
    return (
      partnerRoom instanceof C1_Room &&
      (hasContinuousExteriorWalls(partnerRoom.type) || !isSpecialExteriorRoomType(partnerRoom.type))
    );
  }

  /** Finds the closest projected position within a supplied set of wall segments. */
  private nearestPlacementOnWalls(
    point: { x: number; y: number },
    walls: C1_Wall[],
  ): { wall: C1_Wall; offset: number } | undefined {
    let nearest: { wall: C1_Wall; offset: number; distance: number } | undefined;
    for (const wall of walls) {
      const points = this.wallPoints(wall);
      if (!points) continue;
      const dx = points.end.x - points.start.x;
      const dy = points.end.y - points.start.y;
      const lengthSquared = dx * dx + dy * dy;
      if (lengthSquared === 0) continue;
      const rawOffset = ((point.x - points.start.x) * dx + (point.y - points.start.y) * dy) / lengthSquared;
      const offset = Math.min(Math.max(rawOffset, 0), 1);
      const projected = { x: points.start.x + dx * offset, y: points.start.y + dy * offset };
      const distance = Math.hypot(point.x - projected.x, point.y - projected.y);
      if (!nearest || distance < nearest.distance) nearest = { wall, offset, distance };
    }
    return nearest;
  }

  /** Moves all polygon coordinates matching an axis coordinate by less than one millimetre. */
  moveAlignedPoints(kind: "v" | "h", previous: number, next: number): void {
    const coordinate = kind === "v" ? "x" : "y";
    const start = this.data.startPoint;
    if (Math.abs(start[coordinate] - previous) < C1_COORDINATE_MATCH_TOLERANCE) {
      this.setStartPoint({ ...start, [coordinate]: next });
    }
    if (!this.walls) return;
    for (const wall of this.walls) {
      if (Math.abs(wall.end[coordinate] - previous) >= C1_COORDINATE_MATCH_TOLERANCE) continue;
      wall.setEnd({ ...wall.end, [coordinate]: next });
    }
  }

  /** Translates the complete room polygon while preserving its wall topology. */
  translate(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    this.setStartPoint({ x: this.data.startPoint.x + dx, y: this.data.startPoint.y + dy });
    if (!this.walls) return;
    for (const wall of this.walls) wall.setEnd({ x: wall.end.x + dx, y: wall.end.y + dy });
    this.normalizeWallSegments();
    this.recalculateFloorWallTypes();
  }

  /** Moves only one wall segment; geometrically unrelated points on the same axis remain unchanged. */
  moveWallToCoordinate(wall: C1_Wall, kind: "v" | "h", next: number): void {
    const walls = this.walls;
    if (!walls) return;
    const index = walls.indexOf(wall);
    if (index < 0) return;
    const coordinate = kind === "v" ? "x" : "y";
    const points = this.wallPoints(wall);
    if (!points) return;
    const nextStart = { ...points.start, [coordinate]: next };
    const nextEnd = { ...points.end, [coordinate]: next };

    this.moveWallStart(index, nextStart);
    wall.setEnd(nextEnd);
    if (index === walls.size - 1) this.setStartPoint(nextEnd);
    this.normalizeWallSegments();
  }

  /** Returns every polygon point, including the repeated closing endpoint when present. */
  polygonPoints(): Array<{ x: number; y: number }> {
    const points = [{ ...this.data.startPoint }];
    if (!this.walls) return points;
    for (const wall of this.walls) points.push({ ...wall.end });
    return points;
  }

  /** Whether the polygon can still use the specialized legacy rectangle renderer. */
  isAxisAlignedRectangle(): boolean {
    if (this.walls?.size !== 4) return false;
    const points = this.polygonPoints();
    if (points.length !== 5) return false;
    const tolerance = 0.000001;
    return (
      Math.abs(points[0].y - points[1].y) < tolerance &&
      Math.abs(points[1].x - points[2].x) < tolerance &&
      Math.abs(points[2].y - points[3].y) < tolerance &&
      Math.abs(points[3].x - points[4].x) < tolerance
    );
  }

  /** Returns the rectangular compatibility wall for existing side-based editor code. */
  wallForSide(side: WallSide): C1_Wall | undefined {
    const indexBySide: Record<WallSide, number> = { top: 0, right: 1, bottom: 2, left: 3 };
    return this.walls?.at(indexBySide[side]);
  }

  /** Resolves both endpoints of a wall from the room's ordered polygon. */
  wallPoints(wall: C1_Wall): { start: { x: number; y: number }; end: { x: number; y: number } } | undefined {
    if (wall.freeWall) return { start: { ...wall.start }, end: { ...wall.end } };
    const walls = this.walls;
    if (!walls) return undefined;
    const index = walls.indexOf(wall);
    if (index < 0) return undefined;
    const start = index === 0 ? this.data.startPoint : walls.at(index - 1)?.end;
    if (!start) return undefined;
    return { start: { ...start }, end: { ...wall.end } };
  }

  wallPointAt(wallId: string, offset: number): { x: number; y: number } | undefined {
    const wall = this.walls?.findFirst((candidate) => candidate.id === wallId);
    if (!wall) return undefined;
    const points = this.wallPoints(wall);
    if (!points) return undefined;
    return {
      x: points.start.x + (points.end.x - points.start.x) * offset,
      y: points.start.y + (points.end.y - points.start.y) * offset,
    };
  }

  private setStartPoint(point: { x: number; y: number }): void {
    if (this.data.startPoint.x === point.x && this.data.startPoint.y === point.y) return;
    this.beforeChange("startPoint");
    this._data.startPoint = { ...point };
    this.changed("startPoint");
  }

  private setRectangularWallEndpoints(endpoints: Array<{ x: number; y: number }>): void {
    const walls = this.walls;
    if (!walls) throw new Error("Cannot set room geometry without an ESTADA room owner.");
    while (walls.size < endpoints.length) walls.add(new C1_Wall({}, this.owner));
    while (walls.size > endpoints.length) walls.at(walls.size - 1)?.delete();
    for (let index = 0; index < endpoints.length; index += 1) walls.at(index)?.setEnd(endpoints[index]);
  }

  wallSegments(side: WallSide, _project: C1_Project = c1_state.project): C1_WallSegment[] {
    void _project;
    const result: C1_WallSegment[] = [];
    if (!this.walls) return result;
    const vertical = side === "left" || side === "right";
    for (const wall of this.walls) {
      const points = this.wallPoints(wall);
      if (!points || this.compatibilitySide(wall) !== side) continue;
      result.push({
        start: vertical ? Math.min(points.start.y, points.end.y) : Math.min(points.start.x, points.end.x),
        end: vertical ? Math.max(points.start.y, points.end.y) : Math.max(points.start.x, points.end.x),
        exterior: this.isExteriorSegment(wall),
        inheritedFromNormalRoom: this.wallInheritsExteriorFromNormalRoom(wall),
      });
    }
    result.sort((left, right) => left.start - right.start);
    return result;
  }

  isExteriorWall(side: WallSide, position?: number) {
    if (!this.walls) return true;
    let firstCompatibleWall: C1_Wall | undefined;
    for (const wall of this.walls) {
      const points = this.wallPoints(wall);
      if (!points) continue;
      const wallSide = this.compatibilitySideForWall(
        wall,
        points.end.x - points.start.x,
        points.end.y - points.start.y,
      );
      if (wallSide !== side) continue;
      firstCompatibleWall ??= wall;
      if (position === undefined) continue;
      const vertical = side === "left" || side === "right";
      const start = vertical ? Math.min(points.start.y, points.end.y) : Math.min(points.start.x, points.end.x);
      const end = vertical ? Math.max(points.start.y, points.end.y) : Math.max(points.start.x, points.end.x);
      if (position >= start - C1_COORDINATE_MATCH_TOLERANCE && position <= end + C1_COORDINATE_MATCH_TOLERANCE) {
        return this.isExteriorSegment(wall);
      }
    }
    return firstCompatibleWall ? this.isExteriorSegment(firstCompatibleWall) : true;
  }

  rect(_project: C1_Project = c1_state.project): Rect {
    void _project;
    const points = this.polygonPoints();
    let x1 = Number.POSITIVE_INFINITY;
    let x2 = Number.NEGATIVE_INFINITY;
    let y1 = Number.POSITIVE_INFINITY;
    let y2 = Number.NEGATIVE_INFINITY;
    for (const point of points) {
      if (point.x < x1) x1 = point.x;
      if (point.x > x2) x2 = point.x;
      if (point.y < y1) y1 = point.y;
      if (point.y > y2) y2 = point.y;
    }
    if (!Number.isFinite(x1)) return { x1: 0, x2: 0, y1: 0, y2: 0 };
    return { x1, x2, y1, y2 };
  }

  /** The palette selects a definition; its catalog structure determines the created owner. */
  addPaletteItem(data: C1_FurnitureData, electricalMode: boolean) {
    return electricalMode && isStandaloneEloProfile(data.definitionId)
      ? this.addInteractiveEloItem(data)
      : this.addFurniture(data);
  }

  /** Adopts an ordered union boundary without replacing surviving persistent wall identities. */
  adoptWallBoundary(boundary: Array<{ wall: C1_Wall; start: C1_PointData; end: C1_PointData }>): void {
    if (!this.owner || !this.walls || !boundary.length) return;
    for (const { wall } of boundary) {
      const source = wall.parent as EstadaRoom;
      source.walls.remove(wall);
    }
    this.setStartPoint(boundary[0].start);
    for (const { wall, end } of boundary) {
      wall.parent = this.owner;
      wall.setEnd(end);
      this.walls.add(wall);
    }
  }

  /** Places a standalone electrical device or socket group directly in the ESTADA room collections. */
  addInteractiveEloItem(data: C1_FurnitureData) {
    if (!this.owner || !isStandaloneEloProfile(data.definitionId)) {
      throw new Error("A standalone electrical item requires a room owner and a supported definition.");
    }
    let tree = createEquipmentInstance(data.definitionId, c1_state.project.equipmentStandard,
      getEloItemProfile(data.definitionId), c1_state.settings.buttonDefaults);
    if (isSingleLightDefinitionId(data.definitionId)) {
      tree = syncSingleLight(data.baseProps, tree, data.wallDocked !== undefined, data.definitionId).eloTree;
    }
    if (!tree) throw new Error(`Missing definition for ${data.definitionId}`);
    const facet = materializeInteractiveEloItem(this.owner, tree);
    facet.setInteractivePlacement({
      paletteId: data.definitionId,
      relativePosition: data.relativePosition,
      side: data.side,
      _wallId: data._wallId,
      wallDocked: data.wallDocked,
      rotation: Number(data.baseProps.rotation) || 0,
    });
    return facet;
  }

  addFurniture(data: C1_FurnitureData) {
    const owner = this.owner;
    if (!owner) throw new Error("Cannot add furniture to a C1 room without an ESTADA owner.");

    if (isLightGroupDefinitionId(data.definitionId)) {
      const synchronized = syncLightGroup(data.baseProps, undefined, data.wallDocked !== undefined, data.definitionId);
      const facet = materializeInteractiveEloItem(owner, synchronized.eloTree);
      if (!(facet instanceof C1_Consumer)) throw new Error("A light array requires a light consumer.");
      facet.setLightGroupFurniture({ ...data, baseProps: synchronized.props });
      if (data.baseProps.name?.trim()) facet.consumer.name = data.baseProps.name.trim();
      return facet.lightGroup!;
    }

    const eloTree = createEquipmentInstance(
        data.definitionId,
        c1_state.project.equipmentStandard,
        getEloItemProfile(data.definitionId),
        c1_state.settings.buttonDefaults,
        data.wallDocked !== undefined,
      );
    const furnitureData: C1_FurnitureData = {
      ...data,
      baseProps: { ...data.baseProps },
    };
    const group = new Group(
      {
        name: furnitureData.baseProps.name?.trim() ?? "",
        groupType: "furniture",
      },
      owner,
    );
    const item = new C1_Furniture(furnitureData, group);
    group.c1 = item;
    owner.groups.add(group);
    replaceMaterializedEloDefinitionTree(group, eloTree);
    if (isLightGroupDefinitionId(item.definitionId) || isSingleLightDefinitionId(item.definitionId)) {
      item.setBaseProps(item.baseProps);
    }
    return item;
  }

  removeFurniture(index: number) {
    const furniture = this.furnitures[index];
    furniture?.owner?.delete();
  }

  addOpening(data: C1_OpeningData & { id?: string; name?: string }) {
    const owner = this.owner;
    if (!owner) throw new Error("Cannot add an opening to a C1 room without an ESTADA owner.");

    const project = c1_state.project;
    const doorType = data.kind === "door" ? data.doorType : undefined;
    const openingData = data;
    const estadaOpening = new Group(
      { ...(openingData.id ? { id: openingData.id } : {}), name: openingData.name ?? "", groupType: "opening" },
      owner,
    );
    const opening = new C1_Opening(
      {
        ...data,
        windowShading:
          data.windowShading ??
          (data.kind === "window" || doorType === "garden" ? project?.settings.defaultOpeningShading : undefined),
      },
      estadaOpening,
    );
    estadaOpening.c1 = opening;
    owner.groups.add(estadaOpening);
    if (project) {
      applyOpeningContactDefault(opening, project, project.settings.defaultOpeningContact);
    }
    replaceMaterializedEloDefinitionTree(estadaOpening, opening.eloTree);
    return opening;
  }

  /** Attaches a transient C1 opening to a newly created persistent ESTADA group. */
  attachOpening(opening: C1_Opening) {
    const owner = this.owner;
    if (!owner) throw new Error("Cannot attach an opening to a C1 room without an ESTADA owner.");

    const estadaOpening = new Group({ name: (opening.owner?.name ?? ""), groupType: "opening" }, owner);
    opening.temporaryRoom = undefined;
    estadaOpening.c1 = opening;
    owner.groups.add(estadaOpening);
    applyOpeningContactDefault(opening, c1_state.project, c1_state.project.settings.defaultOpeningContact);
    replaceMaterializedEloDefinitionTree(estadaOpening, opening.eloTree);
    return opening;
  }

  removeOpening(id: string) {
    const owner = this.owner;
    if (!owner) return;
    for (const group of owner.groups) {
      if (group.groupType === "opening" && group.id === id) {
        group.delete();
        return;
      }
    }
  }

  replaceOpenings(data: C1_OpeningData[]) {
    const owner = this.owner;
    if (!owner) return;
    const groups = owner.groups.asArray();
    for (const group of groups) {
      if (group.groupType === "opening") group.delete();
    }
    for (const openingData of data) this.addOpening(openingData);
  }

  toJSON() {
    const { idr: _removedFlag, ...data } = this.data as C1_RoomData & { idr?: boolean };
    return data;
  }
}

type WallEndpoints = {
  start: { x: number; y: number };
  end: { x: number; y: number };
};

/** Compares topology endpoints with the common geometric tolerance. */
function pointsMatch(left: C1_PointData, right: C1_PointData): boolean {
  return (
    Math.abs(left.x - right.x) <= C1_COORDINATE_MATCH_TOLERANCE &&
    Math.abs(left.y - right.y) <= C1_COORDINATE_MATCH_TOLERANCE
  );
}

ObjectFactory.registerClass("c1:C1_Room", (data, owner) => new C1_Room(data, owner) as unknown as BaseModel);
